<template>
  <v-card flat>
    <div v-if="ShowListDraft">
      <!-- Component used to display draft applications -->
      <template v-if="applicationRadioAnswer === -1 || applicationRadioAnswer === 1">
        <ApplicationListDraft
            :draftApplications="draftApplications"
            :step="step"
            :compId="forCompanyId"
            @open="handleApplicationContinue(arguments[0])"
        />
      </template>

      <template v-if="applicationRadioAnswer === -1 || applicationRadioAnswer === 0">
        <h4 class="mb-8 mt-n2">{{ $t('application.newapplication.startNewTitle') }}</h4>
        <ActionButton class="mt-6" 
          @click="questionDialogVisible = true" 
          actionIcon="$sl-apprentice-application"
          :isDisabled="isImitatingUser()"
        >
          {{ $t('application.newapplication.startNewApp') }}
        </ActionButton>
      </template>
      <BottomNav @clicked="clickBack" />
    </div>
    <div v-else>
      <h1>{{ $t('application.newapplication.title') }}</h1>
      <p v-if="isCompanyUser(forCompanyId) && forCompanyId > 0">
        {{ $t('application.newapplication.instructor-title1') }}
      </p>

      <MessageBox class="my-5" type="info">
        <h6>{{ $t('application.newapplication.notice.title') }}</h6>
        <p>{{ $t('application.newapplication.notice.text') }}</p>
        <p v-html="$t('application.newapplication.notice.links', {
          link1: loGet(links, 'application.instructorDispensations', '#'),
          link2: loGet(links, 'application.beingAnInstructor', '#')
        })"></p>
      </MessageBox>

      <div v-if="draftApplications && draftApplications.length > 0">
        <p class="mt-2">{{ $t('application.newapplication.subtitle') }}</p>
      </div>

      <!-- Component used to display applications list -->
      <ApprenticeListApplications
        :applications="applications"
        showApplicationsList="showApplicationsList"
        :step="step"
        @open="handleApplicationContinue(arguments[0])"
      />

      <h4>{{ $t('application.newapplication.startNewTitle') }}</h4>

      <div v-if="isApprentice">
        <p class="mt-2" v-html="$t('application.newapplication.current-apprenticeship')"></p>
      </div>

      <!-- create new application -->
      <ActionButton class="mt-5" 
        @click="draftApplications && draftApplications.length > 0 ? newApplicationDialogVisible = true : newApplication()" 
        actionIcon="$sl-apprentice-application"
        :isDisabled="isImitatingUser()"
      >
        {{ $t('labels.new-applications')}}
      </ActionButton>
    </div>
    <v-dialog persistent v-model="newApplicationDialogVisible" max-width="400px">
      <v-card flat>
        <v-icon class="close" @click.prevent="newApplicationDialogVisible = false">$sl-close</v-icon>
        <h2>{{ $t('application.newapplication.newApplicationDialogTitle') }}</h2>
        <v-radio-group
            v-model="applicationRadioAnswer"
            :key="applicationRadioAnswer"
            column
        >
          <v-radio
              name="radio1"
              :label="$t('application.newapplication.radioAnswerYes')"
              :value="1"
              key="1"
          ></v-radio>
          <v-radio
              name="radio2"
              :label="$t('application.newapplication.radioAnswerNo')"
              :value="0"
              key="2"
          ></v-radio>
        </v-radio-group>
        <div class="d-flex justify-end pb-2 pt-4">
          <ActionButton
              :type="'application'"
              smallerText
              :block="false"
              @click="newApplicationDialogVisible = false"
              actionIcon="$sl-cancel"
          >{{ $t('labels.cancel') }}</ActionButton>
          <ActionButton
              :block="false"
              class="ml-4"
              smallerText
              @click="newApplication"
              actionIcon="$sl-save"
              :isDisabled="applicationRadioAnswer === -1"
          >{{ $t('labels.save') }}</ActionButton>
        </div>
      </v-card>
    </v-dialog>
    <v-dialog persistent v-model="questionDialogVisible" max-width="360px">
      <v-card flat>
        <v-icon class="close" @click.prevent="questionDialogVisible = false">$sl-close</v-icon>
        <h2>{{ $t('application.confirmation.title') }}</h2>
        <v-radio-group
            v-model="radioAnswer"
            :key="radioAnswer"
            column
        >
          <v-radio
              name="radio1"
              :label="$t('labels.yes')"
              :value="1"
              key="1"
          ></v-radio>
          <v-radio
              name="radio2"
              :label="$t('labels.no')"
              :value="0"
              key="2"
          ></v-radio>
        </v-radio-group>
        <div class="d-flex justify-space-between pb-2 pt-4">
          <ActionButton
              :type="'application'"
              smallerText
              :block="false"
              @click="questionDialogVisible = false"
              actionIcon="$sl-cancel"
          >{{ $t('labels.cancel') }}</ActionButton>
          <ActionButton
              :block="false"
              class=""
              smallerText
              @click="saveAnswer()"
              actionIcon="$sl-save"
          >{{ $t('labels.save') }}</ActionButton>
        </div>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import API from '@/services/api.js'
import filter from 'lodash/filter'
import {mapActions, mapGetters} from 'vuex'
import ApprenticeListApplications from '@/components/Application/ApprenticeListApplications'
import ApplicationListDraft from '@/components/Application/ApplicationListDraft'
import ActionButton from '@/components/Common/ActionButton'
import BottomNav from '@/components/Application/BottomNav'
import MessageBox from '@/components/Common/MessageBox.vue'
import { get, pick } from 'lodash'

export default {
  name: 'ApplicationNewOrContinue',

  props: {
    step: Number
  },
  components: {
    ApprenticeListApplications,
    ApplicationListDraft,
    ActionButton,
    BottomNav,
    MessageBox
  },
  data() {
    return {
      ShowListDraft: false,
      showApplicationsList: false,
      applications: [],
      draftApplications: [],
      links: require('@/lib/external-links.json'),
      radioAnswer: 0,
      applicationRadioAnswer: -1,
      questionDialogVisible: false,
      newApplicationDialogVisible: false,
      applicationId: { id: -1, isCompany: false },
    }
  },
  created() {
    this.init()
  },

  watch: {
    newApplicationDialogVisible: {
      handler(val) {
        if ( !val ) {
          this.applicationRadioAnswer = -1
        }
      },
      immediate: true
    }
  },

  computed: {
    ...mapGetters('currentuser', ['currentUser', 'isApprentice', 'isCompanyUser', 'isImitatingUser']),
    ...mapGetters('application', ['selectedApplication', 'currentStep']),

    forCompanyId() {
      return this.$route.name === 'CreateCompanyApplications' ? this.$route.params.id : 0
    },
  },
  methods: {
    ...mapActions('application', ['setHaveEducation', 'setSelectedApplication']),
    ...mapActions('currentuser', ['updateCurrentUserData']),
    init() {
      this.listApplications()
    },

    saveAnswer() {
      this.setHaveEducation(this.radioAnswer);
      this.$emit('showApplication', this.applicationId)
    },

    /**
     * @vuese
     * Used to check if there need draft application need  to be shown or create a new one
     */
    newApplication() {
      this.newApplicationDialogVisible = false;
      if (this.draftApplications && this.draftApplications.length > 0 && this.forCompanyId == 0) {
        this.ShowListDraft = true
      } else {
        if(!this.currentUser.companyRoles.length) {
          this.questionDialogVisible = true
        }
        this.ShowListDraft = false

        if(!this.questionDialogVisible) {
          this.$emit('showApplication', {id: -1, isCompany: true})
        }
      }
    },
    /**
     * @vuese
     * Used to hide draft applications component
     */
    clickBack() {
      this.ShowListDraft = false
    },
    handleApplicationContinue(arg) {
      this.applicationId = arg;
      this.setSelectedApplication(this.draftApplications.find(it => it.id === arg.id))

      const apprenticeships = get(this, 'currentUser.apprenticeships', [])
      const companyApplication = get(this, 'applications', []).find(({ id }) => this.applicationId.id === id)

      if ( companyApplication ) {
        const foundEarlierProfession = apprenticeships.some(({ professionId }) => professionId === companyApplication.professionId)
  
        if ( foundEarlierProfession ) {
          const application = { ...companyApplication, ...pick(this.currentUser, ['address1', 'city', 'firstName', 'lastName', 'zip']) }
          this.setSelectedApplication(application)
          return this.$emit('go-to-step', 3)
        } 
      }

      if (this.isApprentice || !this.isCompanyUser(this.forCompanyId)) {
        this.questionDialogVisible = true;
      } else {
        this.saveAnswer();
      }
    },
    /**
     * @vuese
     * Used to fetch the list of applications
     * @arg The first argument is a string representing application type and the second argument is an integer representing company id
     */
    listApplications() {
      const fetchByType = this.forCompanyId ? 'internal' : 'own'

      API.listApplications({type: fetchByType, companyId: this.forCompanyId})
        .then(response => {
          //- Get latest user data
          this.getPerson().then(() => {
            this.applications = response.data.rows
            this.draftApplications = filter(this.applications, ['state.name', 'Utkast'])
  
            this.showApplicationsList = Boolean(response.data.count > 0)
          })
          .catch(error => {
            console.log('There was an error:', error)
          })
        })
        .catch(error => {
          console.log('There was an error:', error)
        })
    },

    getPerson() {
      const id = get(this, 'currentUser.id', null)
      if ( !id ) return

      return API.getPerson(id).then(({ data = {} }) => this.updateCurrentUserData(data))
    }
  }
}
</script>

<style scoped></style>
